import React from 'react';
import {
  type StorefrontInfo,
  storefrontInfoContext,
} from './storefront-info-context';
import { Helmet } from 'react-helmet-async';
import { useSiteConfig } from '../../../config/site-config';
import { useIsAuthenticated } from '../../../auth/user-info.hooks';
import { useApiQuery } from '../../../query';
import { ErrorContentWithThemeX4EC } from '../../../provider/scope/error-views/ErrorContentWithThemeX4EC';
import { Icon } from '@im/ui-lib/zenith/Icon';

type StorefrontInfoProviderProps = {
  children: React.ReactNode;
};

export function StorefrontInfoProvider(
  props: Readonly<StorefrontInfoProviderProps>
) {
  const { children } = props;

  const siteConfig = useSiteConfig();
  const siteConfigStorefrontInfo = siteConfig.storefrontInfo;

  const isAuthenticated = useIsAuthenticated();

  const { data, isPending } = useApiQuery<StorefrontInfo>(
    '/resellermanagement/v1/marketplaces',
    undefined,
    {
      enabled: isAuthenticated,
    }
  );

  if (isAuthenticated && !data) {
    if (isPending) return null;
    return (
      <ErrorContentWithThemeX4EC
        actionButtonProps={{
          children: 'Try again',
          onClick: window.location.reload,
          startIcon: <Icon iconId="RefreshCwOne" />,
        }}
        errorMessage={`Sorry, we're having trouble connecting you to the ${siteConfig.storefrontInfo?.displayName} Storefront right now.`}
      />
    );
  }

  const { Provider } = storefrontInfoContext;
  return (
    <Provider value={data}>
      <Helmet>
        <title>{siteConfigStorefrontInfo?.displayName ?? ''} Storefront</title>
        <link
          href={
            siteConfigStorefrontInfo?.favicon ?? '/x4ec/assets/img/favicon.ico'
          }
          rel="icon"
        />
      </Helmet>
      {children}
    </Provider>
  );
}
